import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { aboutPageCopy } from '../data/mock';
import { SEO, pageSEO } from '../components/SEO';

export const AboutPage = () => {
  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.about} path="/about" />
      {/* Hero Section - Minimal & Impactful */}
      <section className="relative min-h-[60vh] flex items-end pt-32 pb-20">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-dark-purple)]/30 via-[var(--color-charcoal)] to-[var(--color-charcoal)]" />
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-6 block">
              The Philosophy
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
              {aboutPageCopy.opening}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Philosophy Content - Clean Two-Column Layout */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            
            {/* Left Column - The Journey */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <div className="space-y-8">
                {aboutPageCopy.journey.split('\n\n').map((paragraph, index) => (
                  <p 
                    key={index}
                    className="text-lg md:text-xl text-[var(--color-cream)]/75 leading-[1.9]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Decorative Divider */}
              <div className="my-16 flex items-center gap-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-dark-purple)]/50 to-transparent" />
                <div className="w-2 h-2 bg-[var(--color-gold)] rotate-45" />
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-dark-purple)]/50 to-transparent" />
              </div>

              {/* Purpose Section */}
              <div className="space-y-8">
                {aboutPageCopy.purpose.split('\n\n').map((paragraph, index) => (
                  <p 
                    key={index}
                    className="text-lg md:text-xl text-[var(--color-cream)]/75 leading-[1.9]"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Visual & Quotes */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="sticky top-32 space-y-10">
                {/* Featured Image */}
                <div className="relative">
                  <div className="aspect-[4/5] rounded-lg overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1663872585330-437b683914c2?w=800&q=80"
                      alt="Contemplative atmosphere"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 -left-4 w-full h-full border border-[var(--color-deep-red)]/30 rounded-lg -z-10" />
                </div>

                {/* Pull Quote */}
                <div className="p-8 bg-[var(--color-charcoal-light)] rounded-lg border-l-4 border-[var(--color-gold)]">
                  <p className="font-serif text-2xl text-[var(--color-cream)] italic leading-relaxed">
                    "Resilience is not loud. It is quiet, steady, and deeply internal."
                  </p>
                </div>

                {/* Core Values */}
                <div className="space-y-4">
                  <h3 className="font-serif text-xl text-[var(--color-gold)] mb-6">
                    At the Heart of This Work
                  </h3>
                  {[
                    { color: 'deep-red', text: 'Emotional resilience' },
                    { color: 'bottle-green', text: 'Self-acceptance' },
                    { color: 'midnight-blue', text: 'Courage to meet oneself honestly' },
                    { color: 'dark-purple', text: 'Curiosity where there has been fear' },
                  ].map((value, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border-l-2 ${
                        value.color === 'deep-red' 
                          ? 'bg-[var(--color-deep-red)]/10 border-[var(--color-deep-red)]'
                          : value.color === 'bottle-green'
                          ? 'bg-[var(--color-bottle-green)]/10 border-[var(--color-bottle-green)]'
                          : value.color === 'midnight-blue'
                          ? 'bg-[var(--color-midnight-blue)]/10 border-[var(--color-midnight-blue)]'
                          : 'bg-[var(--color-dark-purple)]/10 border-[var(--color-dark-purple)]'
                      }`}
                    >
                      <p className="text-[var(--color-cream)]/80">{value.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing Statement - Full Width */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="space-y-4">
              {aboutPageCopy.closing.split('\n').map((line, index) => (
                <p 
                  key={index}
                  className={`text-xl md:text-2xl lg:text-3xl leading-relaxed ${
                    index === 0 
                      ? 'text-[var(--color-cream)]/60' 
                      : 'text-[var(--color-gold)] font-serif mt-4'
                  }`}
                >
                  {line}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What This Space Is / Isn't */}
      <section className="py-24 lg:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              A Clear Distinction
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              What This Space Is—And What It Isn't
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* What This Is NOT */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 lg:p-10 bg-[var(--color-deep-red)]/10 rounded-lg border border-[var(--color-deep-red)]/20"
            >
              <h3 className="font-serif text-2xl mb-8 text-[var(--color-cream)]">This Is Not</h3>
              <ul className="space-y-4">
                {[
                  'Fortune-telling or prediction',
                  'Performative spirituality',
                  'Quick fixes or instant answers',
                  'Preachy instruction or guru energy',
                  'Fluffy, surface-level content',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="text-[var(--color-deep-red)] text-xl mt-0.5">×</span>
                    <span className="text-[var(--color-cream)]/70 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* What This IS */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 lg:p-10 bg-[var(--color-bottle-green)]/10 rounded-lg border border-[var(--color-bottle-green)]/20"
            >
              <h3 className="font-serif text-2xl mb-8 text-[var(--color-cream)]">This Is</h3>
              <ul className="space-y-4">
                {[
                  'Reflective practice rooted in inquiry',
                  'Grounded, intelligent exploration',
                  'Tools for building emotional resilience',
                  'Space for honest self-examination',
                  'Support for conscious, deliberate living',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="text-[var(--color-bottle-green-light)] text-xl mt-0.5">✓</span>
                    <span className="text-[var(--color-cream)]/70 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About the Guide Section */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal)]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              The Person Behind This Work
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
              About the Guide
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Portrait Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative max-w-xs mx-auto lg:mx-0">
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_tarot-coach/artifacts/85hf5uzy_WhatsAppImage2025-04-27at00.06.24.jpeg"
                    alt="The guide behind The Soul Script"
                    className="w-full h-auto"
                    data-testid="guide-portrait"
                  />
                </div>
                {/* Subtle decorative border */}
                <div className="absolute -bottom-3 -right-3 w-full h-full border border-[var(--color-gold)]/20 rounded-lg -z-10" />
              </div>
            </motion.div>

            {/* Guide Copy */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <div className="space-y-6">
                <p className="text-xl md:text-2xl text-[var(--color-cream)] font-serif leading-relaxed">
                  I'm the person behind The Soul Script.
                </p>
                
                <p className="text-lg text-[var(--color-cream)]/70 leading-[1.9]">
                  This work grew from lived experience, long seasons of inner work, and a quiet calling to serve. My journey has been shaped by learning how to sit with uncertainty, build emotional resilience, and develop a deeper relationship with myself—especially through moments that required courage, self-acceptance, and reflection.
                </p>
                
                <p className="text-lg text-[var(--color-cream)]/70 leading-[1.9]">
                  I don't offer answers or certainty. I don't predict outcomes or prescribe solutions. What I offer is space—for inquiry, reflection, and steadiness.
                </p>
                
                <p className="text-lg text-[var(--color-cream)]/70 leading-[1.9]">
                  My role is not to lead you, but to walk alongside you as you learn to listen to yourself more clearly, meet your inner life with honesty, and make choices rooted in awareness rather than impulse.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-charcoal)] via-[var(--color-dark-purple)]/20 to-[var(--color-charcoal)]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              Ready to Begin?
            </h2>
            <p className="text-xl text-[var(--color-cream)]/70 mb-10 leading-relaxed">
              Explore the offerings and find the path that calls to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/coaching"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-semibold rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 group"
              >
                Explore Coaching
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/tarot"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[var(--color-gold)] text-[var(--color-gold)] rounded-full hover:bg-[var(--color-gold)] hover:text-[var(--color-charcoal)] transition-all duration-300"
              >
                Explore Tarot
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
